import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { BookOpen, Plus, X, ArrowUpRight, ArrowDownRight } from "lucide-react";

const fmtGs = (n) =>
  new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n || 0);

export default function LibroCaja() {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [movs, setMovs] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const [m, r] = await Promise.all([
        api.get("/caja", { params: { fecha_desde: fecha, fecha_hasta: fecha } }),
        api.get("/caja/resumen", { params: { fecha } }),
      ]);
      setMovs(m.data);
      setResumen(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [fecha]);

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1400px]">
      <PageHeader
        eyebrow="Contabilidad"
        title="Libro de caja"
        description="Movimientos de ingresos y egresos del consultorio."
        action={
          <div className="flex items-center gap-2">
            <input
              data-testid="caja-fecha"
              type="date"
              className="input-clinic max-w-[180px]"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
            <button data-testid="nuevo-mov-btn" onClick={() => setModalOpen(true)} className="btn-primary-pill">
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo movimiento
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <ResumenCard testId="caja-ingresos" label="Ingresos del día" value={fmtGs(resumen?.ingresos)} accent="sage" icon={ArrowUpRight} />
        <ResumenCard testId="caja-egresos" label="Egresos del día" value={fmtGs(resumen?.egresos)} accent="terracotta" icon={ArrowDownRight} />
        <ResumenCard testId="caja-saldo" label="Saldo del día" value={fmtGs(resumen?.saldo)} accent="neutral" icon={BookOpen} />
      </div>

      <div className="card-clinic overflow-hidden">
        {movs.length === 0 ? (
          <div className="text-sm text-neutral-500 py-14 text-center">
            No hay movimientos registrados para esta fecha.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/50">
                {["Tipo", "Concepto", "Método", "Monto", "Registrado por"].map((h, i) => (
                  <th key={i} className="overline text-left px-5 py-3.5 text-neutral-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movs.map((m) => (
                <tr key={m.id} className="border-b border-neutral-200 row-hover">
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        m.tipo === "ingreso"
                          ? "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                          : "bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta-hover))]"
                      }`}
                    >
                      {m.tipo}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-medium text-neutral-900">{m.concepto}</td>
                  <td className="px-5 py-3 capitalize text-neutral-600">{m.metodo}</td>
                  <td
                    className={`px-5 py-3 font-semibold ${
                      m.tipo === "ingreso" ? "text-[hsl(var(--sage-hover))]" : "text-[hsl(var(--terracotta))]"
                    }`}
                  >
                    {m.tipo === "ingreso" ? "+" : "-"} {fmtGs(m.monto)}
                  </td>
                  <td className="px-5 py-3 text-neutral-600">{m.registrado_por_nombre || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <MovimientoModal
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            load();
          }}
          fecha={fecha}
        />
      )}
    </div>
  );
}

function ResumenCard({ label, value, accent, icon: Icon, testId }) {
  const accents = {
    sage: "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]",
    terracotta: "bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta-hover))]",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  return (
    <div className="card-clinic p-6" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="overline mt-5 mb-1">{label}</div>
      <div className="text-2xl font-bold tracking-tight text-neutral-900">{value}</div>
    </div>
  );
}

function MovimientoModal({ onClose, onSaved, fecha }) {
  const [tipo, setTipo] = useState("ingreso");
  const [monto, setMonto] = useState("");
  const [concepto, setConcepto] = useState("");
  const [metodo, setMetodo] = useState("efectivo");
  const [fechaMov, setFechaMov] = useState(fecha);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    const m = Number(monto);
    if (!m || m <= 0 || !concepto.trim()) {
      toast.error("Completá concepto y monto válido");
      return;
    }
    setLoading(true);
    try {
      await api.post("/caja", { tipo, monto: m, concepto, metodo, fecha: fechaMov });
      toast.success("Movimiento registrado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md border border-neutral-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-bold tracking-tight">Nuevo movimiento</h3>
          <button data-testid="mov-modal-close" onClick={onClose} className="btn-ghost-pill p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Tipo</label>
            <div className="flex gap-2">
              {[
                { v: "ingreso", l: "Ingreso" },
                { v: "egreso", l: "Egreso" },
              ].map((o) => (
                <button
                  key={o.v}
                  data-testid={`mov-tipo-${o.v}`}
                  onClick={() => setTipo(o.v)}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    tipo === o.v
                      ? o.v === "ingreso"
                        ? "border-[hsl(var(--sage))] bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                        : "border-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta-hover))]"
                      : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Concepto</label>
            <input data-testid="mov-concepto" className="input-clinic" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Monto</label>
              <input data-testid="mov-monto" type="number" className="input-clinic" value={monto} onChange={(e) => setMonto(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Fecha</label>
              <input data-testid="mov-fecha" type="date" className="input-clinic" value={fechaMov} onChange={(e) => setFechaMov(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Método</label>
            <select data-testid="mov-metodo" className="input-clinic" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="otro">Otro</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50/50 rounded-b-2xl">
          <button onClick={onClose} className="btn-outline-pill">
            Cancelar
          </button>
          <button data-testid="mov-guardar" onClick={save} disabled={loading} className="btn-primary-pill">
            {loading ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
